import { useEffect, useState } from "react";
import { BusyLabel } from "@/components/loading";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLegalModal } from "@/components/LegalModal";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Mail, RefreshCw, ArrowLeft } from "lucide-react";
import { ID_TYPES, digitsOnly } from "@/components/EditProfileCard";
import { avatarBus } from "@/components/avatar/HitAvatar";

export default function Register() {
  const [step, setStep] = useState("form");  // "form" | "otp"
  const [pendingEmail, setPendingEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  // Pre-selecciona el tipo "store" cuando llegamos desde el botón
  // "Quiero ser tienda aliada" (?tipo=tienda). Así el flujo de registro
  // arranca directo en la pestaña Tienda sin fricción.
  const initialAccountType = (() => {
    try {
      const tipo = new URLSearchParams(window.location.search).get("tipo");
      return tipo === "tienda" ? "store" : "person";
    } catch { return "person"; }
  })();
  const [form, setForm] = useState({
    email: "", password: "", name: "",
    id_type: "cedula", id_number: "",
    ruc: "",
    phone: "", city: "", province: "", birthdate: "", consent_lopdp: false,
    account_type: initialAccountType, store_name: "", public_display: "store",
  });
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loading, setLoading] = useState(false);
  const { loginWithToken } = useAuth();
  const { open: openLegal } = useLegalModal();
  const nav = useNavigate();

  const idCfg = ID_TYPES.find(t => t.value === form.id_type) || ID_TYPES[0];

  useEffect(() => {
    api.get("/geo/provinces").then((r) => setProvinces(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!form.province) { setCities([]); return; }
    setLoadingCities(true);
    api.get("/geo/cities", { params: { province: form.province } })
      .then((r) => setCities(r.data))
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  }, [form.province]);
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const upd = (k, v) => setForm((f) => {
    if (k === "province") return { ...f, province: v, city: "" };
    if (k === "id_type") {
      // Reset id_number when switching so we don't keep an incompatible value.
      return { ...f, id_type: v, id_number: "" };
    }
    return { ...f, [k]: v };
  });

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.consent_lopdp) { toast.error("Debes aceptar el consentimiento LOPDP"); return; }
    if (!form.province || !form.city) { toast.error("Selecciona provincia y ciudad"); return; }
    // Identity validation (fail-fast client-side; server also validates)
    if (idCfg.numeric) {
      if (form.id_number.length !== idCfg.digits) {
        toast.error(`${idCfg.label} debe tener ${idCfg.digits} dígitos`);
        return;
      }
    } else if (form.id_number.trim().length < 5) {
      toast.error("Pasaporte inválido (mínimo 5 caracteres)");
      return;
    }
    if (form.account_type === "store" && (form.ruc || "").length !== 13) {
      toast.error("RUC de la tienda debe tener 13 dígitos");
      return;
    }
    if (form.phone.length < 7) { toast.error("Teléfono debe tener al menos 7 dígitos"); return; }
    setLoading(true);
    try {
      // Backend also mirrors id_number -> cedula for backward compat; we send both.
      const payload = {
        ...form,
        cedula: form.id_type === "cedula" ? form.id_number : form.id_number,
        ruc: form.ruc || null,
      };
      const r = await api.post("/auth/register", payload);
      if (r.data?.pending && r.data?.email) {
        setPendingEmail(r.data.email);
        setStep("otp");
        setResendCooldown(30);
        toast.success("Enviamos un código a tu correo");
      } else {
        // Should not happen — backend always returns {pending, email}. Guard so
        // user is never stuck on a silent frozen screen.
        toast.error("Respuesta inesperada del servidor. Intenta de nuevo.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Error al crear cuenta. Revisa los datos e intenta de nuevo.";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error("Ingresa los 6 dígitos del código"); return; }
    setOtpLoading(true);
    try {
      const r = await api.post("/auth/verify-otp", { email: pendingEmail, code: otp });
      if (!r?.data?.token) {
        toast.error("Respuesta inesperada del servidor. Intenta de nuevo.");
        return;
      }
      await loginWithToken(r.data.token, { id: r.data.user_id, role: r.data.role });
      toast.success("¡Bienvenido a HitTCG! Tu cuenta ya está verificada. Puedes comprar y publicar.");
      nav("/");
      sessionStorage.setItem("hittcg:avatar-prompt-shown", "1");
      setTimeout(() => avatarBus.openPicker("registro"), 600);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Código incorrecto. Intenta de nuevo.";
      toast.error(msg);
    } finally { setOtpLoading(false); }
  };

  const resend = async () => {
    if (resendCooldown > 0) return;
    try {
      const r = await api.post("/auth/resend-otp", { email: pendingEmail });
      setResendCooldown(30);
      toast.success(`Código reenviado. ${r.data?.resends_left ?? 0} reenvíos restantes.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "No se pudo reenviar. Intenta de nuevo.";
      toast.error(msg);
    }
  };

  /** Simple text field for names, emails, addresses. NOT for numeric data. */
  const field = (label, k, type = "text", required = true, placeholder = "") => (
    <div key={k}>
      <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">{label}</Label>
      <Input data-testid={`reg-${k}`} type={type} value={form[k]} onChange={(e) => upd(k, e.target.value)} required={required} placeholder={placeholder} className="bg-[#171717] border-[#262626] text-white" />
    </div>
  );

  if (step === "otp") {
    return (
      <div className="max-w-md mx-auto px-4 py-12">
        <div className="flex justify-center mb-8"><Logo /></div>
        <div className="bg-[#111] border border-[#262626] rounded-md p-8" data-testid="otp-step">
          <button type="button" onClick={() => setStep("form")} className="text-neutral-500 text-xs mb-4 flex items-center gap-1 hover:text-white" data-testid="otp-back">
            <ArrowLeft size={12} />Volver al formulario
          </button>
          <div className="flex items-center gap-2 mb-2">
            <Mail size={24} className="text-[#EF4444]" />
            <h1 className="font-display text-3xl">VERIFICA TU CORREO</h1>
          </div>
          <p className="text-neutral-400 text-sm mb-6">
            Enviamos un código de 6 dígitos a <b className="text-white">{pendingEmail}</b>. Ingresalo para activar tu cuenta. Expira en 10 minutos.
          </p>
          <form onSubmit={verifyOtp} className="space-y-4">
            <div>
              <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Código</Label>
              <Input
                data-testid="otp-code"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                autoFocus
                className="bg-[#171717] border-[#262626] text-white font-mono text-2xl tracking-[0.5em] text-center py-6"
                placeholder="000000"
                required
              />
            </div>
            <Button data-testid="otp-submit" type="submit" disabled={otpLoading || otp.length !== 6} className="w-full bg-[#EF4444] hover:bg-[#DC2626]">
              <BusyLabel busy={otpLoading} text="Creando cuenta…">Activar cuenta</BusyLabel>
            </Button>
          </form>
          <div className="mt-4 pt-4 border-t border-[#262626] flex items-center justify-between">
            <span className="text-xs text-neutral-500">¿No recibiste el código?</span>
            <button
              type="button"
              data-testid="otp-resend"
              onClick={resend}
              disabled={resendCooldown > 0}
              className="text-xs text-[#EF4444] hover:underline disabled:text-neutral-600 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <RefreshCw size={12} />{resendCooldown > 0 ? `Reenviar en ${resendCooldown}s` : "Reenviar código"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="flex justify-center mb-8"><Logo /></div>
      <div className="bg-[#111] border border-[#262626] rounded-md p-8">
        <h1 className="font-display text-3xl mb-2">CREAR CUENTA</h1>
        <p className="text-neutral-400 text-sm mb-8">Verificaremos tu correo con un código de 6 dígitos. Si eres persona, tu cuenta queda <b className="text-emerald-400">verificada al instante</b> con tu cédula — sin subir fotos. Debes tener 18+ años.</p>
        <form onSubmit={submitForm} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 flex gap-2 p-1 bg-[#0A0A0A] border border-[#262626] rounded-md">
            <button type="button" data-testid="reg-account-person" onClick={() => upd("account_type", "person")}
              className={`flex-1 py-2 text-sm rounded ${form.account_type === "person" ? "bg-[#EF4444] text-white" : "text-neutral-400 hover:text-white"}`}>Persona</button>
            <button type="button" data-testid="reg-account-store" onClick={() => upd("account_type", "store")}
              className={`flex-1 py-2 text-sm rounded ${form.account_type === "store" ? "bg-purple-600 text-white" : "text-neutral-400 hover:text-white"}`}>Tienda</button>
          </div>
          {form.account_type === "store" && (
            <>
              <div className="sm:col-span-2 p-3 bg-purple-500/10 border border-purple-500/30 rounded text-xs text-purple-200">
                Registro como <b>TIENDA</b>: RUC obligatorio. En la verificación deberás subir documento del RUC (SRI) y certificado bancario. UN RUC = UNA cuenta.
              </div>
              {field("Nombre de la tienda", "store_name", "text", true, "Ej: HitCards Ecuador")}
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Aparecer públicamente como</Label>
                <select
                  data-testid="reg-public-display"
                  value={form.public_display}
                  onChange={(e) => upd("public_display", e.target.value)}
                  className="w-full bg-[#171717] border border-[#262626] text-white rounded-md h-10 px-3"
                >
                  <option value="store">Nombre de la tienda</option>
                  <option value="owner">Nombre del encargado</option>
                </select>
              </div>
            </>
          )}
          {field(form.account_type === "store" ? "Nombre del encargado" : "Nombre completo", "name")}
          {field("Email", "email", "email")}
          {field("Contraseña", "password", "password")}
          <div>
            <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Teléfono</Label>
            <Input
              data-testid="reg-phone"
              inputMode="numeric"
              pattern="\d*"
              value={form.phone}
              onChange={(e) => upd("phone", digitsOnly(e.target.value, 15))}
              required
              placeholder="Solo dígitos (móvil o fijo)"
              className="bg-[#171717] border-[#262626] text-white font-mono"
            />
          </div>
          <div>
            <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Tipo de identificación</Label>
            <select
              data-testid="reg-id-type"
              value={form.id_type}
              onChange={(e) => upd("id_type", e.target.value)}
              className="w-full bg-[#171717] border border-[#262626] text-white rounded-md h-10 px-3"
            >
              {ID_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">
              {idCfg.value === "pasaporte" ? "Número de pasaporte" : idCfg.label}
            </Label>
            <Input
              data-testid="reg-id-number"
              inputMode={idCfg.numeric ? "numeric" : "text"}
              pattern={idCfg.numeric ? "\\d*" : undefined}
              value={form.id_number}
              onChange={(e) => {
                const v = idCfg.numeric
                  ? digitsOnly(e.target.value, idCfg.digits)
                  : e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 20);
                upd("id_number", v);
              }}
              required
              placeholder={idCfg.numeric ? `${idCfg.digits} dígitos` : "Ej: X1234567"}
              className="bg-[#171717] border-[#262626] text-white font-mono"
            />
          </div>
          {form.account_type === "store" && (
            <div>
              <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">RUC de la tienda (13 dígitos)</Label>
              <Input
                data-testid="reg-ruc"
                inputMode="numeric"
                pattern="\d*"
                value={form.ruc}
                onChange={(e) => upd("ruc", digitsOnly(e.target.value, 13))}
                required
                placeholder="13 dígitos"
                className="bg-[#171717] border-[#262626] text-white font-mono"
              />
            </div>
          )}
          <div>
            <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Provincia</Label>
            {/* Native <select> — Shadcn/Radix Select causes a black-overlay bug
                on iOS Safari (Portal leaves `pointer-events: none` on body when
                closing quickly). Native select opens the iOS wheel picker and
                is bulletproof. */}
            <select
              data-testid="reg-province"
              value={form.province}
              onChange={(e) => upd("province", e.target.value)}
              required
              className="w-full bg-[#171717] border border-[#262626] text-white rounded-md h-10 px-3"
            >
              <option value="">Selecciona tu provincia</option>
              {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Ciudad</Label>
            <select
              data-testid="reg-city"
              value={form.city}
              onChange={(e) => upd("city", e.target.value)}
              disabled={!form.province || loadingCities}
              required
              className="w-full bg-[#171717] border border-[#262626] text-white rounded-md h-10 px-3 disabled:opacity-50"
            >
              <option value="">
                {!form.province ? "Primero elige provincia" : loadingCities ? "Cargando ciudades…" : `Elige una ciudad (${cities.length})`}
              </option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {field("Fecha de nacimiento", "birthdate", "date")}
          <div className="sm:col-span-2 mt-2 flex items-start gap-3 p-4 bg-[#0A0A0A] border border-[#262626] rounded-md">
            <Checkbox id="consent" data-testid="reg-consent" checked={form.consent_lopdp}
              onCheckedChange={(v) => upd("consent_lopdp", !!v)}
              className="mt-0.5 border-[#404040] data-[state=checked]:bg-[#EF4444] data-[state=checked]:border-[#EF4444]" />
            <label htmlFor="consent" className="text-xs text-neutral-400">
              Acepto el tratamiento de mis datos personales conforme a la <button type="button" data-testid="reg-privacy-link" onClick={() => openLegal("privacy")} className="text-[#EF4444] hover:underline">Política LOPDP</button>.
            </label>
          </div>
          <div className="sm:col-span-2">
            <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full bg-[#EF4444] hover:bg-[#DC2626]">
              <BusyLabel busy={loading} text="Enviando código…">Continuar (te enviaremos un código)</BusyLabel>
            </Button>
          </div>
        </form>
        <div className="mt-6 pt-6 border-t border-[#262626] text-sm text-neutral-400 text-center">
          ¿Ya tienes cuenta? <Link to="/login" className="text-[#EF4444] hover:underline">Ingresa</Link>
        </div>
      </div>
    </div>
  );
}
